import {
	DocumentSigningRoleType,
	DocumentSigningSecurityLevel,
	DocumentWorkflowInstanceSignatureStatus,
} from '@dealstack/shared/lib/generated';
import { isSignedSignatureStatus } from '@dealstack/shared/lib/common/business/signatureStatus';
import { methodsForSecurityLevel } from '@dealstack/shared/lib/common/eideasy/methodsForSecurityLevel';
import type { EidEasySigningMethod } from '@dealstack/shared/lib/common/eideasy/signingMethods';
import type { FormPair } from './types';

/**
 * True once the form's own signature has completed. Uses the canonical `isSignedSignatureStatus`
 * so the investor and manager views can never disagree about a row.
 */
export const isKycFormSigned = (form: Pick<FormPair, 'signatureStatus'>): boolean =>
	isSignedSignatureStatus(form.signatureStatus);

/**
 * True when the form should show a "Sign" action. `PENDING_SIGNATURE` is the only status prepare
 * accepts; `fileId` is seeded with the blank template, so it never signals "has been submitted".
 */
export const canSignKycForm = (
	form: Pick<FormPair, 'requiresSignature' | 'signatureStatus' | 'signatureId'>
): boolean =>
	form.requiresSignature &&
	form.signatureStatus === DocumentWorkflowInstanceSignatureStatus.PENDING_SIGNATURE &&
	!!form.signatureId;

/**
 * `setKycFormSigningEnabled` always creates the KYC config at ADVANCED with one PARTY signer, and
 * `InvestorKycFormRes` exposes neither. Source these from the response if that ever varies.
 */
export const KYC_FORM_SIGNING_SECURITY_LEVEL = DocumentSigningSecurityLevel.ADVANCED;
export const KYC_FORM_SIGNER_ROLE_TYPE = DocumentSigningRoleType.PARTY;

/**
 * KYC signing methods, via the same `methodsForSecurityLevel` every other signing surface uses —
 * ADVANCED excludes DRAW, which `enableSimpleSign` still opens outside production (DC-920).
 */
export const getKycFormAllowedSigningMethods = (isProduction: boolean): EidEasySigningMethod[] =>
	methodsForSecurityLevel(KYC_FORM_SIGNING_SECURITY_LEVEL, KYC_FORM_SIGNER_ROLE_TYPE, {
		enableSimpleSign: !isProduction,
	});
